import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Filters = Partial<{
  companyId: string[];
  carrierId: string[];
  supplierId: string[];
  indicatorStatus: string[];
  invoiceNumber: string;
  cargoIdentifier: string;
  emissionDateFrom: string;
  emissionDateTo: string;
  deadlineDate: string;
  sameCarrierCnpj: boolean;
  originUf: string;
  originIbge: string;
  destinationUf: string;
  destinationIbge: string;
}>;

export type PaginationControl = {
  page: number;
  limit: number;
  orderBy?: string;
  orderByDirection?: 'asc' | 'desc';
};

type State = {
  filters: Filters;
  reportIdentifier?: string;
  reportColumns: string[];
  paginationControl: PaginationControl;
};

type Actions = {
  updateFilters: (filters: Filters) => void;
  updatePaginationControl: (paginationControl: Partial<PaginationControl>) => void;
  updateReportIdentifier: (reportIdentifier: string | undefined) => void;
  updateReportColumns: (reportColumns: string[]) => void;
  clearState: () => void;
};

type Options = {
  persisted?: boolean;
}

const getInitialState = (): State => ({
  filters: {},
  reportIdentifier: undefined,
  reportColumns: [],
  paginationControl: {
    page: 1,
    limit: 10,
  },
});

const PERSISTENCE_OPTIONS = {
  name: 'trips-tracking-search-params',
  version: 1,
};

const store = create<State & Actions>(
  (set) => ({
    ...getInitialState(),
    updateFilters: (filters) => set({ filters }),
    updateReportIdentifier: (reportIdentifier) => {
      return set({ reportIdentifier });
    },
    updateReportColumns: (reportColumns) => {
      return set({ reportColumns });
    },
    updatePaginationControl: (paginationControl) => {
      return set((state) => ({
        paginationControl: {
          ...state.paginationControl,
          ...paginationControl,
        },
      }));
    },
    clearState: () => set(getInitialState()),
  }),
);

const persistStore = create(
  persist<State & Actions>(
    (set) => ({
      ...getInitialState(),
      updateFilters: (filters) => set({ filters }),
      updateReportIdentifier: (reportIdentifier) => {
        return set({ reportIdentifier });
      },
      updateReportColumns: (reportColumns) => {
        return set({ reportColumns });
      },
      updatePaginationControl: (paginationControl) => {
        return set((state) => ({
          paginationControl: {
            ...state.paginationControl,
            ...paginationControl,
          },
        }));
      },
      clearState: () => set(getInitialState()),
    }),
    PERSISTENCE_OPTIONS,
  ),
);

export const useTripsTrackingSearchParams = (options?: Options) => {
  return options?.persisted ? persistStore() : store();
};